import { useConfirmation } from "shared/components/ConfirmationProvider";
import React, { useState, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import {
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  Search,
  Person,
  Delete,
  Edit,
  Add,
  Close,
  DirectionsCar,
  TwoWheeler,
  Schedule,
  EventBusy,
  Person as UserIcon,
  Visibility,
  FilterList,
  CheckCircle,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Clear,
  Info,
  Download as DownloadIcon,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";
import UsuarioDialog from "../ModalAdmin/UsuarioDialog";
import SuccessDialog from "../ModalAdmin/SuccessDialog";

const vehicleTypes = {
  1: "Vehículo",
  2: "Moto",
};

// Expresiones regulares para validaciones
const PLATE_REGEX = /^[A-Z]{3}\d{3}$|^[A-Z]{3}\d{2}[A-Z]$/;
const CEDULA_REGEX = /^\d+$/;
const NAME_REGEX = /^[A-Za-záéíóúÁÉÍÓÚñÑ\s]+$/;

const TableUsuariosParqueadero = forwardRef(
  (
    {
      usuarios = [],
      loading = false,
      error = null,
      onRefresh = null,
      onAddUsuario = null,
      onEditUsuario = null,
      onDeleteUsuario = null,
      currentUser = null,
      hasEditPermission = false,
      hasDeletePermission = false,
      hasCreatePermission = false,
    },
    ref
  ) => {
    const confirm = useConfirmation();

    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [dateRange, setDateRange] = useState({
      start: "",
      end: "",
    });
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState("");
    const [selectedUsuario, setSelectedUsuario] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [createdUser, setCreatedUser] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    const isDateFilterActive = dateRange.start !== "" || dateRange.end !== "";

    // Función para formatear fecha para mostrar en el filtro
    const formatDateDisplay = (dateString) => {
      if (!dateString) return "";

      const parts = dateString.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        const date = new Date(Date.UTC(year, month, day));
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: "UTC",
        });
      }
      return dateString;
    };

    // Función para obtener solo la fecha (sin hora) de un string de fecha
    const getDateOnly = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toISOString().split("T")[0];
    };

    const isUserActive = (usuario) => {
      if (!usuario.end_date) return false;
      const now = new Date();
      const endDate = new Date(usuario.end_date);
      return endDate > now;
    };

    const columns = [
      { Header: "#", accessor: "index", width: "5%" },
      { Header: "Placa", accessor: "plate", width: "10%" },
      { Header: "Tipo", accessor: "vehicleType", width: "7%" },
      { Header: "Nombre", accessor: "name", width: "10%" },
      { Header: "Apellido", accessor: "lastname", width: "10%" },
      { Header: "Cédula", accessor: "cedula", width: "10%" },
      { Header: "Residencial", accessor: "residential", width: "10%" },
      { Header: "Fecha Inicio", accessor: "start_date", width: "10%" },
      { Header: "Fecha Fin", accessor: "end_date", width: "10%" },
      { Header: "Creado por", accessor: "userGoper", width: "10%" },
      { Header: "Estado", accessor: "status", width: "8%" },
      { Header: "Acciones", accessor: "actions", width: "10%" },
    ];

    const renderDateTime = (dateTime) => {
      if (!dateTime) return "---";
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "---";
      return date.toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const renderStatus = (usuario) => {
      const active = isUserActive(usuario);
      return (
        <Chip
          label={active ? "ACTIVO" : "EXPIRADO"}
          size="small"
          color={active ? "success" : "error"}
          icon={active ? <Schedule /> : <EventBusy />}
          sx={{
            fontWeight: "bold",
            fontSize: "0.65rem",
            height: "26px",
            "& .MuiChip-icon": {
              fontSize: "0.9rem",
            },
          }}
        />
      );
    };

    const renderActions = (usuario) => {
      return (
        <Box display="flex" gap={0.5}>
          {hasEditPermission && (
            <Tooltip title="Editar">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditClick(usuario)}
                sx={{
                  "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasDeletePermission && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(usuario)}
                sx={{
                  "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    };

    const renderVehicleType = (idvehicletype) => {
      const type = vehicleTypes[idvehicletype] || "Desconocido";
      return (
        <Chip
          label={type}
          size="small"
          color={idvehicletype === 1 ? "primary" : "warning"}
          sx={{ fontSize: "0.7rem", height: "26px" }}
        />
      );
    };

    const renderUserGoper = (userGoper) => {
      if (!userGoper)
        return (
          <MDTypography variant="body2" color="text.secondary">
            ---
          </MDTypography>
        );
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <UserIcon fontSize="small" color="info" />
          <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {userGoper}
          </MDTypography>
        </Box>
      );
    };

    const handleFilterChange = (event, newFilterType) => {
      if (newFilterType !== null) {
        setFilterType(newFilterType);
      }
    };

    const handleDateRangeChange = (field, value) => {
      setDateRange((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const clearDateRange = () => {
      setDateRange({
        start: "",
        end: "",
      });
    };

    const getFilteredUsuarios = () => {
      let filtered = [...usuarios];

      // Filtro por estado (activo/expired)
      if (filterType === "active") {
        filtered = filtered.filter((usuario) => isUserActive(usuario));
      } else if (filterType === "expired") {
        filtered = filtered.filter((usuario) => !isUserActive(usuario));
      }

      // Filtro por rango de fechas usando solo la fecha (sin hora)
      if (dateRange.start || dateRange.end) {
        filtered = filtered.filter((usuario) => {
          if (!usuario.start_date) return false;

          const userDate = getDateOnly(usuario.start_date);
          if (!userDate) return false;

          if (dateRange.start && userDate < dateRange.start) return false;
          if (dateRange.end && userDate > dateRange.end) return false;

          return true;
        });
      }

      // Filtro por búsqueda
      if (!searchQuery.trim()) return filtered;
      const query = searchQuery.toLowerCase().trim();
      return filtered.filter((usuario) => {
        const searchableFields = [
          usuario.plate?.toLowerCase() || "",
          usuario.name?.toLowerCase() || "",
          usuario.lastname?.toLowerCase() || "",
          usuario.cedula?.toLowerCase() || "",
          usuario.residential?.toLowerCase() || "",
          usuario.UserGoper?.toLowerCase() || "",
          usuario.start_date ? renderDateTime(usuario.start_date).toLowerCase() : "",
          usuario.end_date ? renderDateTime(usuario.end_date).toLowerCase() : "",
          isUserActive(usuario) ? "activo" : "expirado",
        ];
        return searchableFields.some((field) => field.includes(query));
      });
    };

    // Calcular estadísticas del filtro
    const getFilterStats = () => {
      const filtered = getFilteredUsuarios();
      return {
        total: filtered.length,
        active: filtered.filter((u) => isUserActive(u)).length,
        expired: filtered.filter((u) => !isUserActive(u)).length,
        uniquePlates: new Set(filtered.map((u) => u.plate)).size,
      };
    };

    const filteredData = getFilteredUsuarios();
    const activeCount = usuarios.filter((u) => isUserActive(u)).length;
    const expiredCount = usuarios.filter((u) => !isUserActive(u)).length;
    const filterStats = isDateFilterActive ? getFilterStats() : null;
    const hasFilters = searchQuery || filterType !== "all" || isDateFilterActive;

    // ============================================================
    // FUNCIÓN DE EXPORTACIÓN A EXCEL
    // ============================================================
    const handleExportExcel = () => {
      try {
        setIsExporting(true);

        if (!filteredData || filteredData.length === 0) {
          alert("No hay datos para exportar");
          setIsExporting(false);
          return;
        }

        const excelData = filteredData.map((usuario, index) => ({
          "#": index + 1,
          Placa: usuario.plate || "Sin placa",
          Tipo: vehicleTypes[usuario.idvehicletype] || "Desconocido",
          Nombre: usuario.name || "N/A",
          Apellido: usuario.lastname || "N/A",
          Cédula: usuario.cedula || "N/A",
          Residencial: usuario.residential || "N/A",
          "Fecha Inicio": renderDateTime(usuario.start_date),
          "Fecha Fin": renderDateTime(usuario.end_date),
          "Creado por": usuario.UserGoper || "---",
          Estado: isUserActive(usuario) ? "ACTIVO" : "EXPIRADO",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Ajustar ancho de columnas
        worksheet["!cols"] = [
          { wch: 6 }, // #
          { wch: 14 }, // Placa
          { wch: 12 }, // Tipo
          { wch: 18 }, // Nombre
          { wch: 18 }, // Apellido
          { wch: 15 }, // Cédula
          { wch: 22 }, // Residencial
          { wch: 22 }, // Fecha Inicio
          { wch: 22 }, // Fecha Fin
          { wch: 18 }, // Creado por
          { wch: 12 }, // Estado
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios");

        // Nombre del archivo con fecha
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(today.getDate()).padStart(2, "0")}`;

        let fileName = `usuarios_parqueadero_${dateStr}`;
        if (dateRange.start && dateRange.end) {
          fileName = `usuarios_${dateRange.start}_a_${dateRange.end}`;
        } else if (dateRange.start) {
          fileName = `usuarios_desde_${dateRange.start}`;
        } else if (dateRange.end) {
          fileName = `usuarios_hasta_${dateRange.end}`;
        } else if (filterType === "active") {
          fileName = `usuarios_activos_${dateStr}`;
        } else if (filterType === "expired") {
          fileName = `usuarios_expirados_${dateStr}`;
        }

        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      } catch (err) {
        console.error("Error al exportar a Excel:", err);
        alert("Ocurrió un error al generar el archivo Excel");
      } finally {
        setIsExporting(false);
      }
    };

    const rows = filteredData.map((usuario, index) => ({
      index: (
        <MDTypography variant="body2" color="text.secondary">
          {index + 1}
        </MDTypography>
      ),
      plate: (
        <Box display="flex" alignItems="center" gap={1}>
          <Person fontSize="small" color="primary" />
          <MDTypography variant="body1" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
            {usuario.plate || "Sin placa"}
          </MDTypography>
        </Box>
      ),
      name: <MDTypography variant="body2">{usuario.name || "N/A"}</MDTypography>,
      lastname: <MDTypography variant="body2">{usuario.lastname || "N/A"}</MDTypography>,
      cedula: <MDTypography variant="body2">{usuario.cedula || "N/A"}</MDTypography>,
      residential: <MDTypography variant="body2">{usuario.residential || "N/A"}</MDTypography>,
      start_date: renderDateTime(usuario.start_date),
      end_date: renderDateTime(usuario.end_date),
      vehicleType: renderVehicleType(usuario.idvehicletype),
      userGoper: renderUserGoper(usuario.UserGoper),
      status: renderStatus(usuario),
      actions: renderActions(usuario),
    }));

    const handleAddClick = () => {
      setDialogType("add");
      setSelectedUsuario(null);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
      setOpenDialog(true);
    };

    const handleEditClick = (usuario) => {
      setDialogType("edit");
      setSelectedUsuario(usuario);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
      setOpenDialog(true);
    };

    const handleDeleteClick = (usuario) => {
      setDialogType("delete");
      setSelectedUsuario(usuario);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
      setOpenDialog(true);
    };

    const handleDialogClose = async () => {
      if (isSubmitting) {
        if (await confirm("La operación está en progreso. ¿Desea cancelar?")) {
          setIsSubmitting(false);
          setOpenDialog(false);
        }
      } else {
        setOpenDialog(false);
        setSelectedUsuario(null);
        setSubmitStatus(null);
        setStatusMessage("");
        setIsSubmitting(false);
      }
    };

    const handleDialogConfirm = async (formData) => {
      if (dialogType === "delete") {
        setIsSubmitting(true);
        setStatusMessage("Eliminando usuario...");
        setSubmitStatus(null);

        try {
          const result = await onDeleteUsuario(selectedUsuario.id, selectedUsuario.plate);
          if (result && result.success) {
            setSubmitStatus("success");
            setStatusMessage("Usuario eliminado exitosamente");
            setTimeout(() => {
              handleDialogClose();
            }, 1500);
          } else {
            setSubmitStatus("error");
            setStatusMessage(result?.error || "Error al eliminar el usuario");
          }
        } catch (error) {
          setSubmitStatus("error");
          setStatusMessage("Error al eliminar el usuario");
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      setIsSubmitting(true);
      setSubmitStatus(null);
      setStatusMessage(dialogType === "add" ? "Agregando usuario..." : "Actualizando usuario...");

      try {
        let result;
        if (dialogType === "add") {
          const newUsuario = {
            ...formData,
            UserGoper: formData.UserGoper || currentUser?.username || "",
          };
          result = await onAddUsuario(newUsuario);

          if (result && result.success) {
            setCreatedUser(result.data || newUsuario);
          }
        } else if (dialogType === "edit") {
          const updatedUsuario = {
            ...selectedUsuario,
            ...formData,
            UserGoper:
              formData.UserGoper || selectedUsuario.UserGoper || currentUser?.username || "",
          };
          result = await onEditUsuario(updatedUsuario);
        }

        if (result && result.success) {
          setSubmitStatus("success");
          setStatusMessage(
            dialogType === "add"
              ? "Usuario agregado exitosamente"
              : "Usuario actualizado exitosamente"
          );

          if (dialogType === "add") {
            setTimeout(() => {
              handleDialogClose();
              setOpenSuccessDialog(true);
            }, 500);
          } else {
            setTimeout(() => {
              handleDialogClose();
            }, 1500);
          }
        } else {
          setSubmitStatus("error");
          setStatusMessage(result?.error || "Error al realizar la operación");
        }
      } catch (error) {
        setSubmitStatus("error");
        setStatusMessage(
          dialogType === "add" ? "Error al agregar el usuario" : "Error al actualizar el usuario"
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleSearchChange = (e) => {
      setSearchQuery(e.target.value);
    };

    const clearSearch = () => {
      setSearchQuery("");
    };

    const resetFilters = () => {
      setSearchQuery("");
      setFilterType("all");
      setDateRange({
        start: "",
        end: "",
      });
    };

    useImperativeHandle(ref, () => ({
      openAddModal: () => {
        if (hasCreatePermission) {
          handleAddClick();
        }
      },
    }));

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Box textAlign="center">
            <CircularProgress color="primary" size={40} />
            <MDTypography variant="h6" mt={2}>
              Cargando usuarios...
            </MDTypography>
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            <MDTypography variant="h6" fontWeight="bold">
              Error al cargar usuarios
            </MDTypography>
            <MDTypography variant="body1">{error}</MDTypography>
          </Alert>
          <Box display="flex" justifyContent="center" mt={2}>
            <MDButton variant="contained" color="primary" onClick={onRefresh}>
              Reintentar carga
            </MDButton>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        {/* Buscador y Filtros */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            {/* Buscador */}
            <Box sx={{ flex: 1, minWidth: "180px" }}>
              <MDInput
                fullWidth
                placeholder="Buscar por placa, nombre, cédula..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9e9e9e", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <Close fontSize="small" sx={{ color: "#9e9e9e" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0d47a1",
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                  },
                }}
              />
            </Box>

            {/* Filtros de Fecha - TAMAÑO DUPLICADO */}
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                label="Fecha Inicio"
                type="date"
                value={dateRange.start}
                onChange={(e) => handleDateRangeChange("start", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: dateRange.end || undefined,
                }}
                sx={{
                  minWidth: "200px",
                  width: "220px",
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                    fontWeight: 500,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0d47a1",
                  },
                }}
              />
              <MDTypography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                a
              </MDTypography>
              <TextField
                label="Fecha Fin"
                type="date"
                value={dateRange.end}
                onChange={(e) => handleDateRangeChange("end", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dateRange.start || undefined,
                }}
                sx={{
                  minWidth: "200px",
                  width: "220px",
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                    fontWeight: 500,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0d47a1",
                  },
                }}
              />
              {isDateFilterActive && (
                <Tooltip title="Limpiar filtro de fechas">
                  <IconButton
                    size="small"
                    onClick={clearDateRange}
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "white",
                      height: "48px",
                      width: "48px",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                        borderColor: "#d32f2f",
                      },
                    }}
                  >
                    <Clear sx={{ color: "#d32f2f", fontSize: "1.4rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Filtros de estado */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <FilterList sx={{ color: "#757575", fontSize: "1.1rem" }} />
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                aria-label="Filtro de usuarios"
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.6,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    color: "#757575",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(13, 71, 161, 0.04)",
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#0d47a1",
                      color: "white !important",
                      borderColor: "#0d47a1",
                      boxShadow: "0 2px 8px rgba(13, 71, 161, 0.2)",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                        color: "white !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white !important",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all" aria-label="Todos">
                  <Visibility fontSize="small" sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                  Todos ({usuarios.length})
                </ToggleButton>
                <ToggleButton value="active" aria-label="Activos">
                  <CheckCircle fontSize="small" sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                  Activos ({activeCount})
                </ToggleButton>
                <ToggleButton value="expired" aria-label="Expirados">
                  <ErrorIcon fontSize="small" sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                  Expirados ({expiredCount})
                </ToggleButton>
              </ToggleButtonGroup>

              {/* ============================================================
                  BOTÓN DE DESCARGA DE EXCEL
              ============================================================ */}
              <Tooltip title="Descargar Excel con los datos filtrados">
                <span>
                  <MDButton
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleExportExcel}
                    disabled={isExporting || filteredData.length === 0}
                    startIcon={<DownloadIcon />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      px: 2.5,
                      py: 0.8,
                      backgroundColor: "#2e7d32",
                      boxShadow: "0 2px 8px rgba(46, 125, 50, 0.2)",
                      "&:hover": {
                        backgroundColor: "#1b5e20",
                        boxShadow: "0 4px 12px rgba(46, 125, 50, 0.3)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#9e9e9e",
                      },
                    }}
                  >
                    {isExporting ? "Exportando..." : "Exportar Excel"}
                  </MDButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Mostrar estadísticas del filtro */}
          {isDateFilterActive && filterStats && (
            <Box
              mt={2}
              pt={2}
              borderTop="1px solid #e0e0e0"
              display="flex"
              alignItems="center"
              gap={3}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Info fontSize="small" color="info" />
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  Filtro aplicado:
                </Typography>
              </Box>
              <Chip
                label={`Total: ${filterStats.total}`}
                size="small"
                color="default"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Activos: ${filterStats.active}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Expirados: ${filterStats.expired}`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Placas únicas: ${filterStats.uniquePlates}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "medium" }}>
                {dateRange.start && dateRange.end
                  ? `Del ${formatDateDisplay(dateRange.start)} al ${formatDateDisplay(
                      dateRange.end
                    )}`
                  : dateRange.start
                  ? `Desde ${formatDateDisplay(dateRange.start)}`
                  : `Hasta ${formatDateDisplay(dateRange.end)}`}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Tabla */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
            autoResetPage={false}
            paginationResetKey={JSON.stringify([
              searchQuery,
              filterType,
              dateRange.start,
              dateRange.end,
            ])}
            showTotalEntries={true}
            noEndBorder={true}
            canSearch={false}
            pagination={{ variant: "gradient", color: "primary" }}
            sx={{
              "& .MuiTableRow-root": {
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(13, 71, 161, 0.02)",
                },
              },
              "& .MuiTableCell-head": {
                backgroundColor: "#f5f7fa",
                fontWeight: 600,
                color: "#424242",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Paper>

        {/* Mensaje cuando no hay datos - DEBAJO DE LA TABLA */}
        {filteredData.length === 0 && (
          <Box p={4} textAlign="center">
            <Search sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
            <MDTypography variant="h5" fontWeight="bold" gutterBottom>
              {hasFilters ? "No se encontraron resultados" : "No hay usuarios registrados"}
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary">
              {hasFilters
                ? `No hay usuarios que coincidan con los filtros aplicados.`
                : "No se encontraron usuarios en el sistema."}
            </MDTypography>
            <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
              {hasFilters && (
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={resetFilters}
                  startIcon={<RefreshIcon />}
                  sx={{ borderRadius: "8px" }}
                >
                  Mostrar Todos
                </MDButton>
              )}
              {!hasFilters && hasCreatePermission && (
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handleAddClick}
                  startIcon={<Add />}
                  sx={{ borderRadius: "8px" }}
                >
                  Agregar Nuevo Usuario
                </MDButton>
              )}
            </Box>
          </Box>
        )}

        <UsuarioDialog
          open={openDialog}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          dialogType={dialogType}
          selectedUsuario={selectedUsuario}
          currentUser={currentUser}
          isSubmitting={isSubmitting}
          submitStatus={submitStatus}
          statusMessage={statusMessage}
        />

        <SuccessDialog
          open={openSuccessDialog}
          onClose={() => {
            setOpenSuccessDialog(false);
            setCreatedUser(null);
          }}
          userData={createdUser}
          autoClose={true}
          autoCloseDelay={3000}
        />
      </Box>
    );
  }
);

TableUsuariosParqueadero.propTypes = {
  usuarios: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
  onAddUsuario: PropTypes.func,
  onEditUsuario: PropTypes.func,
  onDeleteUsuario: PropTypes.func,
  currentUser: PropTypes.object,
  hasEditPermission: PropTypes.bool,
  hasDeletePermission: PropTypes.bool,
  hasCreatePermission: PropTypes.bool,
};

TableUsuariosParqueadero.defaultProps = {
  usuarios: [],
  loading: false,
  error: null,
  onRefresh: null,
  onAddUsuario: null,
  onEditUsuario: null,
  onDeleteUsuario: null,
  currentUser: null,
  hasEditPermission: false,
  hasDeletePermission: false,
  hasCreatePermission: false,
};

export default TableUsuariosParqueadero;
